import React from "react";
import { Grid } from "@mui/material";

import Flx from "../../layout/Flx";
import Htag from "../../typography/Htag";
import { isNil } from "lodash";
import Txt from "../../typography/Txt";

/**
 * RffFormSection
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - Content of the form section
 * @param {string} [props.title] - Section title (optional)
 * @param {string} [props.description] - Section description (optional)
 * @param {string} [props.titleType='h4'] - Heading type for the title (e.g. 'h1', 'h2', 'h3', 'h4') if Htag supports it
 * @param {boolean} [props.disableGrid=false] - If true, content won't be rendered in a MUI Grid container
 * @param {boolean} [props.isGridChild=false] - If true, wraps the section in a <Grid item xs={12}/> so it can be placed within a Grid container
 * @param {boolean} [props.suppressBottomMargin = false] - Optional style overrides (MUI sx prop)
 * @param {number} [props.columnSpacing=2] - The spacing between columns in the Grid
 * @param {number} [props.rowSpacing=3.2] - The spacing between rows in the Grid
 * @param {object} [props.sx] - Optional style overrides (MUI sx prop)
 */
const RffGroup = ({
  children,
  title,
  suppressBottomMargin,
  titleType,
  // titleType = "h2",
  size = 12,
  description,
  headerEndContent,
  disableGrid = false,
  isGridChild = false,
  columnSpacing = 2.4,
  rowSpacing = 4.2,
  headerSx,
  // rowSpacing = 3.8,
  // rowSpacing = 4.8,
  // columnSpacing = 2,
  // rowSpacing = 3.2,
  sx,
  sectionSx,
}) => {
  const content = (
    <ContentWrapper
      title={title}
      titleType={titleType}
      disableGrid={disableGrid}
      columnSpacing={columnSpacing}
      suppressBottomMargin={suppressBottomMargin}
      headerEndContent={headerEndContent}
      description={description}
      rowSpacing={rowSpacing}
      headerSx={headerSx}
      sx={sx}
      sectionSx={sectionSx}
    >
      {children}
    </ContentWrapper>
  );

  // If this section is part of a larger Grid, wrap it in <Grid item xs={12}/>
  if (isGridChild) {
    return (
      <Grid item xs={size}>
        {content}
      </Grid>
    );
  }

  // Otherwise, just render the content directly
  return content;
};

const ContentWrapper = ({
  title,
  titleType,
  disableGrid,
  columnSpacing,
  rowSpacing,
  sx,
  suppressBottomMargin,
  sectionSx,
  headerSx,
  headerEndContent,
  description,
  children,
}) => {
  return (
    <Flx
      column
      grow
      fw
      className={suppressBottomMargin ? "" : "rff-group"}
      sx={{
        h3: {
          // fontSize: "18px !important",
          // lineHeight: "24px !important",
          fontSize: "18px",
          lineHeight: "24px",
        },
        ...sx,
      }}
    >
      {isNil(title) ? null : (
        <Flx jw jb ac sx={{ mb: 2, ...headerSx }}>
          <Flx column>
            {title && titleType && (
              <Htag
                {...{ [titleType]: true }}
                sx={{
                  fontSize: "24px",
                  lineHeight: "36px",
                }}
              >
                {title}
              </Htag>
            )}
            {title && isNil(titleType) && <Txt bold>{title}</Txt>}
            {description && (
              <Txt
                secondary
                sx={
                  {
                    // fontSize: "14px",
                    // lineHeight: "20px",
                    // mb: 2,
                  }
                }
              >
                {description}
              </Txt>
            )}
          </Flx>
          {headerEndContent}
        </Flx>
      )}
      <SectionContent
        disableGrid={disableGrid}
        columnSpacing={columnSpacing}
        rowSpacing={rowSpacing}
        sx={sectionSx}
      >
        {children}
      </SectionContent>
    </Flx>
  );
};

const SectionContent = ({
  disableGrid,
  columnSpacing,
  rowSpacing,
  sx,
  children,
}) => {
  if (disableGrid) {
    return (
      <Flx column fw sx={{ gap: "14px", ...sx }}>
        {children}
      </Flx>
    );
  }

  return (
    <Grid
      container
      columnSpacing={columnSpacing}
      rowSpacing={rowSpacing}
      sx={sx}
    >
      {children}
    </Grid>
  );
};

export default RffGroup;
